import { useState, useEffect, useCallback, useRef } from "react";
import { getLessonList } from "../../api/lesson";

const useLessonLists = (storeId, searchDate, allView, selectInstructorId, groupLessonView, groupLessonAllView) => {
  const [lessonList, setLessonList] = useState([]);
  const [lessonListLoading, setLessonListLoading] = useState(true);
  const [error, setError] = useState(null);
  // 월별 데이터 캐시 (키: "YYYY-MM", 값: 데이터 배열)
  const cacheRef = useRef(new Map());
  // 캐시 키 생성 함수
  const getCacheKey = useCallback((date, instructorId, isGroup, isAllGroup) => {
    return `${date}_${instructorId || 'all'}_${isGroup}_${isAllGroup}`;
  }, []);
  // 초기 로딩 여부 추적
  const hasLoadedOnceRef = useRef(false);

  // 데이터 합치기 및 중복 제거 헬퍼 함수
  const combineAndDeduplicateData = useCallback((prevData, currentData, nextData) => {
    const allData = [...prevData, ...(currentData || []), ...nextData];

    // id를 기준으로 중복 제거 (나중에 온 데이터가 우선)
    const uniqueDataMap = new Map();
    allData.forEach(lesson => {
      if (lesson && lesson.id) {
        uniqueDataMap.set(lesson.id, lesson);
      }
    });
    return Array.from(uniqueDataMap.values());
  }, []);

  // 현재 월 기준으로 이전/다음 월 계산
  const getAdjacentMonths = useCallback((date) => {
    const [year, month] = date.split('-').map(Number);

    // 이전 월
    const prevDate = new Date(year, month - 2, 1);
    const prevYear = prevDate.getFullYear();
    const prevMonth = String(prevDate.getMonth() + 1).padStart(2, "0");
    const prevMonthDate = `${prevYear}-${prevMonth}`;

    // 다음 월
    const nextDate = new Date(year, month, 1);
    const nextYear = nextDate.getFullYear();
    const nextMonth = String(nextDate.getMonth() + 1).padStart(2, "0");
    const nextMonthDate = `${nextYear}-${nextMonth}`;

    return { prevMonthDate, currentMonthDate: date, nextMonthDate };
  }, []);

  // 특정 월의 데이터를 백그라운드로 가져오기
  const fetchMonthDataInBackground = useCallback(async (monthDate, currentSearchDate) => {
    if (!storeId || (!allView && selectInstructorId == null)) return;

    const cacheKey = getCacheKey(monthDate, allView ? null : selectInstructorId, groupLessonView, groupLessonAllView);

    // 이미 캐시에 있으면 스킵
    if (cacheRef.current.has(cacheKey)) return;

    try {
      let data;
      if (allView) {
        data = await getLessonList(storeId, monthDate, null, groupLessonView, groupLessonAllView);
      } else {
        data = await getLessonList(storeId, monthDate, selectInstructorId, groupLessonView, groupLessonAllView);
      }
      // 캐시에 저장
      cacheRef.current.set(cacheKey, data || []);

      // 백그라운드 데이터 로드 후 현재 표시 중인 데이터 업데이트
      if (currentSearchDate) {
        const { prevMonthDate, currentMonthDate, nextMonthDate } = getAdjacentMonths(currentSearchDate);
        const prevCacheKey = getCacheKey(prevMonthDate, allView ? null : selectInstructorId, groupLessonView, groupLessonAllView);
        const currentCacheKey = getCacheKey(currentMonthDate, allView ? null : selectInstructorId, groupLessonView, groupLessonAllView);
        const nextCacheKey = getCacheKey(nextMonthDate, allView ? null : selectInstructorId, groupLessonView, groupLessonAllView);

        const prevData = cacheRef.current.get(prevCacheKey) || [];
        const currentData = cacheRef.current.get(currentCacheKey) || [];
        const nextData = cacheRef.current.get(nextCacheKey) || [];

        const combinedData = combineAndDeduplicateData(prevData, currentData, nextData);
        setLessonList(combinedData);
      }
    } catch (error) {
      console.error(`Failed to fetch background data for ${monthDate}:`, error);
    }
  }, [storeId, allView, selectInstructorId, groupLessonView, groupLessonAllView, getCacheKey, getAdjacentMonths, combineAndDeduplicateData]);

  const fetchData = useCallback(async () => {
    if (!storeId || (!allView && selectInstructorId == null)) {
      setLessonListLoading(false);
      return;
    }

    const isInitialLoad = !hasLoadedOnceRef.current;
    if (isInitialLoad) {
      setLessonListLoading(true);
    }

    try {
      const { prevMonthDate, currentMonthDate, nextMonthDate } = getAdjacentMonths(searchDate);

      // 현재 월 데이터 가져오기
      const currentCacheKey = getCacheKey(currentMonthDate, allView ? null : selectInstructorId, groupLessonView, groupLessonAllView);
      let currentData = cacheRef.current.get(currentCacheKey);

      if (!currentData) {
        // 캐시에 없으면 가져오기
        if (allView) {
          currentData = await getLessonList(storeId, currentMonthDate, null, groupLessonView, groupLessonAllView);
        } else {
          currentData = await getLessonList(storeId, currentMonthDate, selectInstructorId, groupLessonView, groupLessonAllView);
        }
        cacheRef.current.set(currentCacheKey, currentData || []);
      }

      // 이전 월과 다음 월 데이터는 백그라운드로 미리 가져오기
      const prevCacheKey = getCacheKey(prevMonthDate, allView ? null : selectInstructorId, groupLessonView, groupLessonAllView);
      const nextCacheKey = getCacheKey(nextMonthDate, allView ? null : selectInstructorId, groupLessonView, groupLessonAllView);

      const prevData = cacheRef.current.get(prevCacheKey) || [];
      const nextData = cacheRef.current.get(nextCacheKey) || [];

      // 현재 월 + 이전 월 + 다음 월 데이터 합치기 및 중복 제거
      const combinedData = combineAndDeduplicateData(prevData, currentData, nextData);

      setLessonList(combinedData);
      hasLoadedOnceRef.current = true;

      // 백그라운드로 이전/다음 월 데이터 가져오기 (캐시에 없을 때만)
      if (!cacheRef.current.has(prevCacheKey)) {
        fetchMonthDataInBackground(prevMonthDate, searchDate);
      }
      if (!cacheRef.current.has(nextCacheKey)) {
        fetchMonthDataInBackground(nextMonthDate, searchDate);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLessonListLoading(false);
    }
  }, [storeId, searchDate, allView, selectInstructorId, groupLessonView, groupLessonAllView, getCacheKey, getAdjacentMonths, fetchMonthDataInBackground, combineAndDeduplicateData])

  // 특정 월의 캐시를 무효화하는 함수
  const invalidateCache = useCallback((monthDate) => {
    if (!monthDate) return;

    const [year, month] = monthDate.split('-').map(Number);

    // 해당 월과 이전/다음 월의 캐시 무효화
    const monthsToInvalidate = [];

    // 이전 월
    const prevDate = new Date(year, month - 2, 1);
    const prevYear = prevDate.getFullYear();
    const prevMonth = String(prevDate.getMonth() + 1).padStart(2, "0");
    monthsToInvalidate.push(`${prevYear}-${prevMonth}`);

    // 현재 월
    monthsToInvalidate.push(monthDate);

    // 다음 월
    const nextDate = new Date(year, month, 1);
    const nextYear = nextDate.getFullYear();
    const nextMonth = String(nextDate.getMonth() + 1).padStart(2, "0");
    monthsToInvalidate.push(`${nextYear}-${nextMonth}`);

    // 해당 월들에 대한 모든 캐시 키 무효화
    monthsToInvalidate.forEach(date => {
      const keysToDelete = [];
      cacheRef.current.forEach((value, key) => {
        if (key.startsWith(date)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => cacheRef.current.delete(key));
    });
  }, []);

  // refresh 함수: 캐시를 무효화하고 데이터 다시 가져오기
  const refresh = useCallback((invalidateMonthDate = null) => {
    if (invalidateMonthDate) {
      invalidateCache(invalidateMonthDate);
    } else {
      // 전체 캐시 무효화
      cacheRef.current.clear();
    }
    fetchData();
  }, [fetchData, invalidateCache]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { lessonList, lessonListLoading, error, refresh, invalidateCache };
};

export default useLessonLists;
